import RichContent from '../components/ui/RichContent';
import { getPujaAbout } from '../lib/pujaForm';
import { getYoutubeEmbedUrl } from '../lib/youtube';
import React, { useState, useEffect } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { parseISO, isValid } from 'date-fns';
import {
  Calendar,
  MapPin,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Flame,
  Star,
  Award,
  Globe,
  Check,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import PujaCheckoutModal from '../components/puja/PujaCheckoutModal';
import PujaShareOptions from '../components/puja/PujaShareOptions';

import API from '../config/api';

const DEVOTEE_ILLUSTRATIONS = {
  1: 'https://paramparik-puja-s3-145406344077-eu-north-1-an.s3.eu-north-1.amazonaws.com/uploads/puja/individual-puja.jpg',
  2: 'https://paramparik-puja-s3-145406344077-eu-north-1-an.s3.eu-north-1.amazonaws.com/uploads/puja/partner-puja.jpg',
  3: 'https://paramparik-puja-s3-145406344077-eu-north-1-an.s3.eu-north-1.amazonaws.com/uploads/puja/family-puja.jpg',
  4: 'https://paramparik-puja-s3-145406344077-eu-north-1-an.s3.eu-north-1.amazonaws.com/uploads/puja/family-puja.jpg',
  5: 'https://paramparik-puja-s3-145406344077-eu-north-1-an.s3.eu-north-1.amazonaws.com/uploads/puja/joint-family-puja.jpg',
  6: 'https://paramparik-puja-s3-145406344077-eu-north-1-an.s3.eu-north-1.amazonaws.com/uploads/puja/joint-family-puja.jpg'
};

const DEFAULT_PACKAGES = [
  {
    id: 'pkg_single',
    name: 'Individual Devotee Sankalp',
    price: 951,
    tag: 'Most Chosen',
    devotees_count: 1,
    benefits: [
      'Sankalp with 1 Devotee Name & Gotra',
      'Vedic Archana & Chanting by Tirtha Pandits',
      'Full Puja Video shared on WhatsApp',
      'Blessed Prasad delivered to your address'
    ]
  },
  {
    id: 'pkg_couple',
    name: 'Partner / Couple Sankalp',
    price: 1551,
    tag: 'Popular',
    devotees_count: 2,
    benefits: [
      'Sankalp for 2 Devotees (Husband & Wife / Partners)',
      'Special Abhishek with Gangajal & Cow Milk',
      'Full Puja Video with recitation proof',
      'Consecrated Prasad box delivered home'
    ]
  },
  {
    id: 'pkg_family',
    name: 'Complete Family Samuhik Sankalp',
    price: 2551,
    tag: 'Best Value',
    devotees_count: 4,
    benefits: [
      'Sankalp for up to 4 Family Members with Gotra',
      'Special Deep Daan & Pushparchana',
      'Full HD Video on WhatsApp',
      'Express Sacred Prasad kit dispatched'
    ]
  }
];

export default function PujaDetailPage() {
  const { slug, pujaId } = useParams();
  const identifier = slug || pujaId;

  const [puja, setPuja] = useState(null);
  const [temple, setTemple] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);

  // Language toggle: 'en' | 'hi'
  const [language, setLanguage] = useState('en');

  // Tabs for Rich Content (Point 4)
  const [activeTab, setActiveTab] = useState('about'); // 'about' | 'performed_by' | 'why_perform' | 'video'
  const [openFaq, setOpenFaq] = useState(null); // Accordion state (Point 7)

  // Gallery slider state
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Selected package for booking modal
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);

  // Countdown timer state
  const [timeLeft, setTimeLeft] = useState(null);
  const scheduledDate = (puja?.is_weekly && puja?.calculated_puja_date) || puja?.puja_date || '';
  const [searchParams] = useSearchParams();

  // Handle direct ?book=true or ?package=xxx
  useEffect(() => {
    if (!puja) return;
    const requestedPkgId = searchParams.get('package');
    if (requestedPkgId) {
      const pkgs = (puja.packages && puja.packages.length > 0) ? puja.packages : DEFAULT_PACKAGES;
      const matched = pkgs.find(p => p.id === requestedPkgId || p.name === requestedPkgId);
      if (matched) setSelectedPackage(matched);
    }
    if (searchParams.get('book') === 'true') {
      setIsCheckoutOpen(true);
    }
  }, [puja, searchParams]);

  useEffect(() => {
    const fetchPuja = async () => {
      try {
        setLoading(true);
        setCurrentImageIndex(0);
        setActiveTab('about');
        const res = await axios.get(`${API}/pujas/${identifier}`);
        const pujaData = res.data;
        setPuja(pujaData);

        // Fetch temple details
        if (pujaData.temple_id) {
          try {
            const templeRes = await axios.get(`${API}/temples/${pujaData.temple_id}`);
            setTemple(templeRes.data);
          } catch (tErr) {
            console.warn('Temple not found:', tErr);
          }
        }

        // Fetch products
        try {
          const prodsRes = await axios.get(`${API}/products`);
          const allProds = Array.isArray(prodsRes.data) ? prodsRes.data : prodsRes.data.products || [];
          setRelatedProducts(allProds.slice(0, 4));
        } catch (pErr) {
          console.warn('Products not found:', pErr);
        }

        // Fetch FAQs & Testimonials
        try {
          const [faqsRes, testRes] = await Promise.allSettled([
            axios.get(`${API}/faqs`),
            axios.get(`${API}/testimonials`)
          ]);

          if (Array.isArray(pujaData.faqs) && pujaData.faqs.length > 0) {
            setFaqs(pujaData.faqs);
          } else if (faqsRes.status === 'fulfilled' && Array.isArray(faqsRes.value.data)) {
            setFaqs(faqsRes.value.data.slice(0, 5));
          }

          if (Array.isArray(pujaData.testimonials) && pujaData.testimonials.length > 0) {
            setTestimonials(pujaData.testimonials);
          } else if (testRes.status === 'fulfilled' && Array.isArray(testRes.value.data)) {
            setTestimonials(testRes.value.data.slice(0, 4));
          }
        } catch (fErr) {
          console.warn('FAQs error:', fErr);
        }

      } catch (err) {
        console.error('Error fetching puja:', err);
        toast.error('Puja ceremony not found');
      } finally {
        setLoading(false);
      }
    };

    fetchPuja();
  }, [identifier]);

  useEffect(() => {
    const target = scheduledDate ? parseISO(scheduledDate.length === 10 ? `${scheduledDate}T00:00:00+05:30` : scheduledDate) : null;
    if (!target || !isValid(target)) {
      setTimeLeft(null);
      return;
    }
    const updateTimer = () => {
      const difference = target.getTime() - Date.now();
      setTimeLeft(difference > 0 ? {
        days: Math.floor(difference / 86400000),
        hours: Math.floor((difference % 86400000) / 3600000),
        minutes: Math.floor((difference % 3600000) / 60000),
        seconds: Math.floor((difference % 60000) / 1000)
      } : null);
    };
    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [scheduledDate]);


  const handleSmoothScrollToPackages = () => {
    const target = document.getElementById('participate-section');
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleOpenBooking = (pkg) => {
    if (pkg) setSelectedPackage(pkg);
    setIsCheckoutOpen(true);
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-[#FBF4E7] pt-28 pb-16 flex items-center justify-center">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-[#6E1B34] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-sm font-semibold text-[#4E1225]">Loading Sacred Puja Ceremony...</p>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (!puja) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-[#FBF4E7] pt-28 pb-16 flex items-center justify-center">
          <div className="text-center p-8 bg-white rounded-2xl shadow-sm border border-gray-200 max-w-md">
            <Flame className="w-12 h-12 text-[#FF7B00] mx-auto mb-4" />
            <h2 className="text-xl font-bold text-[#2A1F16] mb-2">Puja Not Found</h2>
            <p className="text-sm text-gray-500 mb-6">The requested puja ceremony may have concluded or been updated.</p>
            <Link to="/puja">
              <Button className="btn-saffron w-full">Explore All Sacred Pujas</Button>
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  // Gallery images array
  const galleryImages = (puja.gallery && puja.gallery.length > 0)
    ? puja.gallery
    : [puja.image_url || temple?.image_url || 'https://images.unsplash.com/photo-1609825488888-3a766db05542?w=800&q=80'];

  const packagesList = (puja.packages && puja.packages.length > 0) ? puja.packages : DEFAULT_PACKAGES;
  const youtubeEmbedUrl = getYoutubeEmbedUrl(puja.youtube_url);

  const dateValue = scheduledDate ? parseISO(scheduledDate.slice(0, 10)) : null;
  const dateLabel = dateValue && isValid(dateValue)
    ? dateValue.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })
    : 'Date to be announced';
  const heroSubtitle = puja.subtitle || [puja.deity, puja.temple_type].filter(Boolean).join(' • ');
  const heroReviews = (puja.testimonials || []).filter(review => Number(review.rating) >= 1 && Number(review.rating) <= 5);
  const averageRating = heroReviews.length
    ? (heroReviews.reduce((sum, review) => sum + Number(review.rating), 0) / heroReviews.length).toFixed(1)
    : null;

  // Language specific fields
  const isHi = language === 'hi';
  const aboutContent = (isHi && getPujaAbout(puja, 'hi')) || getPujaAbout(puja, 'en');
  const pujaPerformedBy = (isHi && puja.puja_performed_by_hi) ? puja.puja_performed_by_hi : (puja.puja_performed_by_en || 'Initiated Vedic Pandits of the holy sanctum with hereditary smarta tradition.');
  const whyPerform = (isHi && puja.why_perform_hi) ? puja.why_perform_hi : (puja.why_perform_en || 'Performing this sacred Vedic ritual dispels planetary doshas, brings peace of mind, family harmony, and divine grace.');

  return (
    <>
      <Navbar />

      <div className="bg-[#FBF4E7] min-h-screen pt-4 sm:pt-6 pb-10 sm:pb-16">
        {/* Breadcrumb */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2">
          <nav className="flex items-center gap-1.5 text-xs text-[#83725B]">
            <Link to="/puja" className="hover:text-[#D9711F] transition-colors">Puja</Link>
            <span>&rsaquo;</span>
            <span className="min-w-0 text-[#2A1F16] font-semibold truncate sm:max-w-md">{puja.name}</span>
          </nav>
        </div>

        {/* Hero: natural-size gallery and ceremony details */}
        <section aria-label="Puja overview" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-3 pb-8 sm:pb-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-10 items-start">
            <div className="min-w-0 space-y-3">
              <div className="relative overflow-hidden rounded-2xl border border-[#D4AF37]/70 bg-[#FBF4E7] shadow-sm">
                <img src={galleryImages[currentImageIndex] || galleryImages[0]} alt={puja.name}
                  className="block w-full h-auto" data-testid="puja-hero-image" />
                {galleryImages.length > 1 && (
                  <>
                    <button type="button" aria-label="Previous puja image"
                      onClick={() => setCurrentImageIndex(prev => (prev - 1 + galleryImages.length) % galleryImages.length)}
                      className="absolute left-3 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-white/90 text-[#4E1225] shadow flex items-center justify-center hover:bg-white">
                      <ChevronLeft className="h-5 w-5" />
                    </button>
                    <button type="button" aria-label="Next puja image"
                      onClick={() => setCurrentImageIndex(prev => (prev + 1) % galleryImages.length)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-white/90 text-[#4E1225] shadow flex items-center justify-center hover:bg-white">
                      <ChevronRight className="h-5 w-5" />
                    </button>
                  </>
                )}
              </div>
              {galleryImages.length > 1 && (
                <div className="flex flex-wrap justify-center gap-1" aria-label="Puja gallery">
                  {galleryImages.map((image, index) => (
                    <button key={index} type="button" aria-label={`Show puja image ${index + 1}`}
                      aria-pressed={currentImageIndex === index} onClick={() => setCurrentImageIndex(index)}
                      className="flex h-7 min-w-7 items-center justify-center rounded focus-visible:ring-2 focus-visible:ring-[#D9711F]">
                      <span className={`h-2 rounded-full transition-all ${currentImageIndex === index ? 'w-7 bg-[#D9711F]' : 'w-2 bg-[#E7D9BC]'}`} />
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="min-w-0 space-y-5">
              <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#2A1F16] leading-snug break-words">{puja.name}</h1>
              
              <div className="space-y-3 pt-2 text-sm sm:text-base text-[#83725B]">
                <p className="flex items-start gap-3">
                  <MapPin className="mt-0.5 h-5 w-5 shrink-0" />
                  <span>{[temple?.name || 'Vedic Shrine', temple?.location || temple?.city].filter(Boolean).join(', ')}</span>
                </p>
                <p className="flex items-start gap-3">
                  <Calendar className="mt-0.5 h-5 w-5 shrink-0" />
                  <span>{dateLabel}{puja.upcoming_tithi ? ` · ${puja.upcoming_tithi}` : ''}</span>
                </p>
              </div>

              {timeLeft && (
                <div className="space-y-2">
                  <p className="text-sm font-semibold text-[#2A1F16]">Puja date starts in</p>
                  <div className="flex flex-wrap gap-2" role="timer" aria-label="Time until puja date">
                    {Object.entries(timeLeft).filter(([unit]) => unit !== 'days' || timeLeft.days > 0).map(([unit, value]) => (
                      <div key={unit} className="inline-flex items-baseline gap-1.5 rounded-lg bg-white px-3 py-2 text-[#D9711F] shadow-sm">
                        <span className="font-price text-xl font-semibold">{String(value).padStart(2, '0')}</span>
                        <span className="text-[10px] uppercase">{unit === 'minutes' ? 'min' : unit === 'seconds' ? 'sec' : unit === 'hours' ? 'hr' : 'days'}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="space-y-5 border-t border-[#E7D9BC]/70 pt-5">
                {heroReviews.length > 0 && (
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div className="flex -space-x-2" aria-hidden="true">
                        {heroReviews.slice(0, 5).map((review, index) => (
                          <span key={index} className="flex h-9 w-9 items-center justify-center rounded-full border-2 border-white bg-[#F3E7D0] text-sm font-bold text-[#6E1B34]">
                            {review.name?.charAt(0) || 'ॐ'}
                          </span>
                        ))}
                      </div>
                      <span className="text-xs font-semibold text-[#6E1B34]">{heroReviews.length} devotee {heroReviews.length === 1 ? 'review' : 'reviews'}</span>
                    </div>
                    <span className="inline-flex items-center gap-1.5 rounded-xl border border-[#E7D9BC] bg-white px-3 py-2 text-sm font-bold text-[#2A1F16] shadow-sm">
                      {averageRating} <Star className="h-4 w-4 fill-[#D9711F] text-[#D9711F]" />
                    </span>
                  </div>
                )}
                <div className="flex items-center gap-2 sm:gap-3">
                  <Button onClick={handleSmoothScrollToPackages} data-testid="select-puja-btn"
                    className="btn-saffron h-12 sm:h-14 flex-1 min-w-0 rounded-2xl text-sm sm:text-base font-bold">
                    Select Puja Package
                  </Button>
                </div>
                <PujaShareOptions puja={puja} />
              </div>
            </div>
          </div>
        </section>

        {/* ═══ PARTICIPATE IN PUJA PACKAGES ═══ */}
        <section id="participate-section" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 scroll-mt-20">
          <div className="border-b border-[#E7D9BC] mb-8 pb-2 flex items-center justify-between flex-wrap gap-2">
            <div>
              <h2 className="font-heading text-xl sm:text-2xl font-bold text-[#2A1F16] pb-1">
                Participate in Puja
              </h2>
              <p className="text-xs text-gray-500">
                Choose the sankalp package suited for your family offering.
              </p>
            </div>
          </div>

          <div data-testid="puja-packages-grid" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {packagesList.map((pkg, idx) => {
              const count = pkg.devotees_count || (idx === 0 ? 1 : idx === 1 ? 2 : 4);
              const artworkUrl = DEVOTEE_ILLUSTRATIONS[count] || DEVOTEE_ILLUSTRATIONS[1];
              const originalPrice = Math.round((pkg.price || 951) * 1.3);

              return (
                <div
                  key={pkg.id || idx}
                  className="bg-white rounded-3xl border-2 border-[#E7D9BC] hover:border-[#FF7B00] shadow-sm hover:shadow-lg transition-all flex flex-col justify-between overflow-hidden"
                >
                  <div>
                    {/* Header */}
                    <div className="p-4 sm:p-5 border-b border-[#E7D9BC] bg-[#FAF8F5] flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <h3 className="font-heading text-base font-bold text-[#4E1225] break-words">
                          {pkg.name}
                        </h3>
                        <p className="text-xs text-[#83725B] font-semibold mt-0.5">
                          {count === 1 ? '1 Devotee' : `Up to ${count} Devotees`}
                        </p>

                        <div className="flex flex-wrap items-baseline gap-2 pt-1">
                          <span className="font-bold text-xl text-[#2A1F16]">
                            ₹{pkg.price?.toLocaleString()}
                          </span>
                          <span className="text-xs text-gray-400 line-through">
                            ₹{originalPrice.toLocaleString()}
                          </span>

                          {pkg.tag && (
                            <span className="text-[10px] font-bold px-2 py-0.5 rounded-md uppercase tracking-wider text-white bg-[#FF7B00]">
                              {pkg.tag}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="relative w-16 h-16 sm:w-20 sm:h-20 flex-shrink-0">
                        <img
                          src={artworkUrl}
                          alt={pkg.name}
                          className="w-full h-full object-cover rounded-full border-2 border-white shadow-md"
                        />
                      </div>
                    </div>

                    {/* Benefits Checklist */}
                    <div className="p-5 space-y-2.5">
                      {(pkg.benefits || [
                        'Sankalp with your Name & Gotra',
                        'Full puja video shared with you',
                        'Live updates via WhatsApp',
                        'Blessed Prasad delivered to your home'
                      ]).map((b, bIdx) => (
                        <div key={bIdx} className="flex items-start gap-2 text-xs text-gray-700">
                          <Check className="w-4 h-4 text-[#2E7D45] flex-shrink-0 mt-0.5" />
                          <span className="leading-snug">{b}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Open the booking contact step */}
                  <div className="p-5 pt-0">
                    <Button
                      onClick={() => handleOpenBooking(pkg)}
                      className="w-full btn-saffron py-3 rounded-xl font-bold text-sm shadow-md"
                    >
                      Book Now
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* ═══ PUJA INFORMATION & VIDEO TABS ═══ */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="bg-white rounded-3xl p-6 sm:p-8 border border-[#E7D9BC] shadow-xs">
            {/* Nav Tabs Bar */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between border-b border-[#E7D9BC] pb-3 mb-6 gap-3">
              <div className="flex min-w-0 max-w-full gap-2 sm:gap-4 overflow-x-auto scrollbar-none">
                <button
                  type="button"
                  onClick={() => setActiveTab('about')}
                  className={`pb-2.5 px-2 text-xs sm:text-sm font-bold transition-all relative whitespace-nowrap cursor-pointer ${
                    activeTab === 'about' ? 'text-[#D9711F]' : 'text-[#83725B] hover:text-[#2A1F16]'
                  }`}
                >
                  {isHi ? 'परिचय - पूजा, मंदिर एवं भगवान' : 'About - Puja, Mandir & Bhagwan'}
                  {activeTab === 'about' && (
                    <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#D9711F]" />
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => setActiveTab('performed_by')}
                  className={`pb-2.5 px-2 text-xs sm:text-sm font-bold transition-all relative whitespace-nowrap cursor-pointer ${
                    activeTab === 'performed_by' ? 'text-[#D9711F]' : 'text-[#83725B] hover:text-[#2A1F16]'
                  }`}
                >
                  {isHi ? 'पूजा किसके द्वारा संपन्न होगी?' : 'Puja Performed By'}
                  {activeTab === 'performed_by' && (
                    <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#D9711F]" />
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => setActiveTab('why_perform')}
                  className={`pb-2.5 px-2 text-xs sm:text-sm font-bold transition-all relative whitespace-nowrap cursor-pointer ${
                    activeTab === 'why_perform' ? 'text-[#D9711F]' : 'text-[#83725B] hover:text-[#2A1F16]'
                  }`}
                >
                  {isHi ? 'यह पूजा क्यों कराएं?' : 'Why Perform This Puja?'}
                  {activeTab === 'why_perform' && (
                    <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#D9711F]" />
                  )}
                </button>
                {youtubeEmbedUrl && (
                  <button type="button" onClick={() => setActiveTab('video')}
                    className={`pb-2.5 px-2 text-xs sm:text-sm font-bold transition-all relative whitespace-nowrap ${activeTab === 'video' ? 'text-[#D9711F]' : 'text-[#83725B] hover:text-[#2A1F16]'}`}>
                    {isHi ? 'पूजा वीडियो' : 'Puja Video'}
                    {activeTab === 'video' && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#D9711F]" />}
                  </button>
                )}
              </div>

              {/* Hindi / English Toggle Switch */}
              <div className="flex items-center gap-2.5 bg-[#FBF4E7] px-3.5 py-1.5 rounded-full border border-[#E7D9BC] self-start sm:self-auto shadow-2xs">
                <Globe className="w-3.5 h-3.5 text-[#D9711F]" />
                <span className="text-xs font-semibold text-[#4E1225]">
                  {isHi ? 'हिंदी सामग्री:' : 'Hindi Content:'}
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={isHi}
                  onClick={() => setLanguage(prev => prev === 'hi' ? 'en' : 'hi')}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none cursor-pointer ${
                    isHi ? 'bg-[#6E1B34]' : 'bg-[#D6C4A5]'
                  }`}
                  title="Toggle Hindi Content"
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition duration-200 shadow-sm ${
                      isHi ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>

            {/* TAB CONTENT */}
            {activeTab === 'video' && youtubeEmbedUrl && (
              <div className="aspect-video w-full overflow-hidden rounded-2xl bg-black">
                <iframe src={youtubeEmbedUrl} title={`${puja.name} video`} className="h-full w-full border-0"
                  loading="lazy" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin" allowFullScreen />
              </div>
            )}
            {activeTab === 'about' && (
              <RichContent value={aboutContent} className="text-sm text-gray-700" />
            )}

            {activeTab === 'performed_by' && (
              <div className="space-y-4">
                <div className="flex items-start gap-4 p-5 rounded-2xl bg-[#FAF8F5] border border-[#E7D9BC]">
                  <div className="w-12 h-12 rounded-xl bg-[#6E1B34] text-white flex items-center justify-center font-bold text-lg flex-shrink-0">
                    ॐ
                  </div>
                  <div className="space-y-2">
                    <h3 className="font-heading text-base font-bold text-[#4E1225]">
                      {isHi ? 'सिद्ध वैदिक आचार्यों द्वारा अनुष्ठान' : 'Initiated Himalayan Vedic Priests'}
                    </h3>
                    <RichContent value={pujaPerformedBy} className="text-xs sm:text-sm text-gray-700" />
                    <div className="flex flex-wrap items-center gap-2 pt-2">
                      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-[11px] font-bold bg-white text-[#2E7D45] border border-emerald-200">
                        <CheckCircle2 className="w-3.5 h-3.5" /> Authentic Veda Shakha Chanting
                      </span>
                      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-[11px] font-bold bg-white text-[#2E7D45] border border-emerald-200">
                        <CheckCircle2 className="w-3.5 h-3.5" /> Individual Sankalp Chanted
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'why_perform' && (
              <div className="space-y-5">
                <div>
                  <h3 className="font-heading text-lg font-bold text-[#4E1225] mb-2 flex items-center gap-2">
                    <Award className="w-4 h-4 text-[#FF7B00]" />
                    {isHi ? 'यह पूजा क्यों कराएं?' : 'Why Perform This Puja?'}
                  </h3>
                  <RichContent value={whyPerform} className="text-xs sm:text-sm text-gray-700" />
                </div>


              </div>
            )}
          </div>
        </section>

        {/* ═══ HOW TO PARTICIPATE (Point 8: Exact 5 Steps) ═══ */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="bg-white rounded-3xl p-6 sm:p-8 border border-[#E7D9BC] shadow-xs space-y-6">
            <div className="text-center max-w-xl mx-auto">
              <span className="text-xs font-bold text-[#D9711F] uppercase tracking-wider bg-[#F3E7D0] px-3 py-1 rounded-full border border-[#E7D9BC]">
                Simple &amp; Sacred Process
              </span>
              <h2 className="font-heading text-xl sm:text-2xl font-bold text-[#4E1225] mt-2">
                How to Participate in Puja?
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
              <div className="bg-[#FAF8F5] p-5 rounded-2xl border border-[#E7D9BC]/70 space-y-2 flex flex-col justify-between">
                <div>
                  <div className="w-8 h-8 rounded-full bg-[#FF7B00] text-white flex items-center justify-center font-bold text-xs mb-3 shadow-xs">
                    1
                  </div>
                  <h3 className="font-heading font-bold text-sm text-[#2A1F16]">Select Puja</h3>
                  <p className="text-xs text-gray-600 mt-1 leading-relaxed">
                    Select a puja and bhet daan options such as vastra daan, gau seva, brahman bhojan etc.
                  </p>
                </div>
              </div>

              <div className="bg-[#FAF8F5] p-5 rounded-2xl border border-[#E7D9BC]/70 space-y-2 flex flex-col justify-between">
                <div>
                  <div className="w-8 h-8 rounded-full bg-[#FF7B00] text-white flex items-center justify-center font-bold text-xs mb-3 shadow-xs">
                    2
                  </div>
                  <h3 className="font-heading font-bold text-sm text-[#2A1F16]">Pay Dakshina</h3>
                  <p className="text-xs text-gray-600 mt-1 leading-relaxed">
                    Securely pay your dakshina using UPI, Card or Net Banking.
                  </p>
                </div>
              </div>

              <div className="bg-[#FAF8F5] p-5 rounded-2xl border border-[#E7D9BC]/70 space-y-2 flex flex-col justify-between">
                <div>
                  <div className="w-8 h-8 rounded-full bg-[#FF7B00] text-white flex items-center justify-center font-bold text-xs mb-3 shadow-xs">
                    3
                  </div>
                  <h3 className="font-heading font-bold text-sm text-[#2A1F16]">Fill sankalp form</h3>
                  <p className="text-xs text-gray-600 mt-1 leading-relaxed">
                    Enter offering name(s), gotra, prasad delivery address &amp; puja wish.
                  </p>
                </div>
              </div>

              <div className="bg-[#FAF8F5] p-5 rounded-2xl border border-[#E7D9BC]/70 space-y-2 flex flex-col justify-between">
                <div>
                  <div className="w-8 h-8 rounded-full bg-[#FF7B00] text-white flex items-center justify-center font-bold text-xs mb-3 shadow-xs">
                    4
                  </div>
                  <h3 className="font-heading font-bold text-sm text-[#2A1F16]">On puja date - Watch puja video</h3>
                  <p className="text-xs text-gray-600 mt-1 leading-relaxed">
                    Short darshan video will be shared on Puja tithi. Full video with name-gotra will be sent on WhatsApp within 3-5 days.
                  </p>
                </div>
              </div>

              <div className="bg-[#FAF8F5] p-5 rounded-2xl border border-[#E7D9BC]/70 space-y-2 flex flex-col justify-between">
                <div>
                  <div className="w-8 h-8 rounded-full bg-[#FF7B00] text-white flex items-center justify-center font-bold text-xs mb-3 shadow-xs">
                    5
                  </div>
                  <h3 className="font-heading font-bold text-sm text-[#2A1F16]">Prasad delivered at Home</h3>
                  <p className="text-xs text-gray-600 mt-1 leading-relaxed">
                    Authentic prasad from the temple will be delivered within 7 - 10 days
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ═══ DYNAMIC CUSTOMER REVIEWS (Point 7) ═══ */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="bg-white rounded-3xl p-6 sm:p-8 border border-[#E7D9BC] shadow-xs">
            <div className="text-center mb-6">
              <span className="text-[11px] sm:text-xs font-bold text-[#D9711F] uppercase tracking-widest">Verified Devotee Experiences</span>
              <h2 className="font-heading text-lg sm:text-2xl font-bold text-[#4E1225] mt-1">What Our Customers Say</h2>
              
              <div className="flex items-center justify-center gap-3 mt-2.5">
                <span className="font-heading text-2xl sm:text-3xl font-bold text-[#4E1225]">4.9</span>
                <div className="text-left">
                  <div className="flex text-[#D9711F]">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-3.5 h-3.5 fill-current" />
                    ))}
                  </div>
                  <span className="text-[10px] sm:text-xs text-[#83725B]">Based on 2,400+ blessed devotees</span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {(testimonials.length > 0 ? testimonials : [
                {
                  name: 'Rajesh & Suman Sharma',
                  location: 'Gurugram, Haryana',
                  rating: 5,
                  quote: 'The video proof with our family gotra was received on WhatsApp as promised. We felt blessed seeing the pandits perform the ritual with such devotion.'
                },
                {
                  name: 'Ananya Deshmukh',
                  location: 'Pune, Maharashtra',
                  rating: 5,
                  quote: 'Prasad arrived securely packed with Ganga jal and sacred vibhuti. Highly authentic traditional service.'
                }
              ]).map((testi, idx) => {
                const initial = testi.name ? testi.name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase() : 'PS';
                return (
                  <div key={idx} className="bg-[#FAF8F5] p-4 rounded-xl border border-[#E7D9BC] flex flex-col justify-between">
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-[#6E1B34] text-white font-bold text-xs flex items-center justify-center">
                            {initial}
                          </div>
                          <div>
                            <h4 className="text-xs font-bold text-[#2A1F16]">{testi.name}</h4>
                            <span className="text-[10px] text-[#83725B]">{testi.location || 'Verified Devotee'}</span>
                          </div>
                        </div>
                        <div className="flex text-[#D9711F]">
                          {[...Array(testi.rating || 5)].map((_, i) => (
                            <Star key={i} className="w-3 h-3 fill-current" />
                          ))}
                        </div>
                      </div>
                      <p className="text-xs text-[#2A1F16] leading-relaxed italic mb-2">
                        "{testi.quote || testi.content}"
                      </p>
                    </div>
                    <div className="flex items-center gap-1 text-[10px] font-bold text-[#2E7D45] pt-1.5 border-t border-[#E7D9BC]/60">
                      <CheckCircle2 className="w-3 h-3" />
                      <span>Verified Devotee Booking</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ═══ FREQUENTLY ASKED QUESTIONS (Point 7) ═══ */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="bg-white rounded-3xl p-6 sm:p-8 border border-[#E7D9BC] shadow-xs">
            <div className="text-center mb-6">
              <span className="text-[11px] sm:text-xs font-bold text-[#D9711F] uppercase tracking-widest">Questions &amp; Answers</span>
              <h2 className="font-heading text-lg sm:text-2xl font-bold text-[#4E1225] mt-1">Frequently Asked Questions</h2>
            </div>

            <div className="max-w-3xl mx-auto space-y-2">
              {(faqs.length > 0 ? faqs : [
                {
                  question: 'Do I need to be physically present at the temple?',
                  answer: 'No, your physical presence is not required. The Vedic rituals and sankalp are performed by qualified priests using your name and gotra. Full video proof will be sent on WhatsApp.'
                },
                {
                  question: 'How and when will I receive the sacred prasad?',
                  answer: 'Blessed prasad from the sanctum is consecrated and dispatched within 48 hours of ceremony completion, arriving safely at your home in 7 to 10 days.'
                },
                {
                  question: 'Can I add offerings like Gau Seva or Vastra Daan?',
                  answer: 'Yes, during checkout you can select special Bhet offerings including Gau Seva, Vastra Daan, and Brahman Bhojan to be offered on your behalf.'
                }
              ]).map((faq, idx) => (
                <div key={idx} className="border border-[#E7D9BC] rounded-xl overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                    className="w-full text-left p-3.5 sm:p-4 font-semibold text-xs sm:text-sm text-[#2A1F16] flex items-center justify-between gap-3 bg-[#FAF8F5]/60 hover:bg-[#FAF8F5] transition-colors cursor-pointer"
                  >
                    <span>{faq.question || faq.q}</span>
                    <ChevronDown className={`w-3.5 h-3.5 text-[#6E1B34] transition-transform flex-shrink-0 ${openFaq === idx ? 'rotate-180' : ''}`} />
                  </button>
                  {openFaq === idx && (
                    <div className="p-3.5 sm:p-4 text-xs text-[#83725B] leading-relaxed bg-white border-t border-[#E7D9BC]">
                      {faq.answer || faq.a}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

      </div>

      {/* Booking Checkout Wizard Modal */}
      <PujaCheckoutModal
        key={`${puja?.id || ''}-${selectedPackage?.id || selectedPackage?.name || packagesList[0]?.id || ''}`}
        isOpen={isCheckoutOpen}
        onClose={() => setIsCheckoutOpen(false)}
        puja={puja}
        temple={temple}
        selectedPackage={selectedPackage || packagesList[0]}
      />

      <Footer />
    </>
  );
}
